import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SocialIconsComponent, SocialIcon } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { AlignLeft, AlignCenter, AlignRight, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

interface SocialIconsSettingsProps {
  component: SocialIconsComponent;
}

const PLATFORM_OPTIONS: SocialIcon['platform'][] = [
  'instagram',
  'twitter',
  'facebook',
  'tiktok',
  'youtube',
  'telegram',
  'whatsapp',
  'line',
  'openchat',
  'threads',
  'linkedin',
  'pinterest',
  'twitch',
  'kick',
  'custom',
];

export const SocialIconsSettings: React.FC<SocialIconsSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<SocialIconsComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const handleIconUpdate = (index: number, updates: Partial<SocialIcon>) => {
    const newIcons = [...component.settings.icons];
    newIcons[index] = { ...newIcons[index], ...updates };
    handleUpdate({ icons: newIcons });
  };

  const handleAddIcon = () => {
    const newIcons = [
      ...component.settings.icons,
      { id: `icon-${Date.now()}`, platform: 'instagram' as const, url: 'https://instagram.com/yourusername' },
    ];
    handleUpdate({ icons: newIcons });
  };

  const handleRemoveIcon = (index: number) => {
    const newIcons = component.settings.icons.filter((_, i) => i !== index);
    handleUpdate({ icons: newIcons });
  };

  const handleMoveIcon = (index: number, direction: 'up' | 'down') => {
    const newIcons = [...component.settings.icons];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    [newIcons[index], newIcons[newIndex]] = [newIcons[newIndex], newIcons[index]];
    handleUpdate({ icons: newIcons });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium">{t('settings.socialIcons.heading')}</label>
          <button
            onClick={handleAddIcon}
            className="p-1 bg-blue-600 hover:bg-blue-700 rounded text-white"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="space-y-3">
          {component.settings.icons.map((icon, index) => (
            <div key={icon.id} className="p-3 bg-gray-800 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleMoveIcon(index, 'up')}
                    disabled={index === 0}
                    className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={t('settings.common.moveUp')}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => handleMoveIcon(index, 'down')}
                    disabled={index === component.settings.icons.length - 1}
                    className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={t('settings.common.moveDown')}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                {/* Platform select */}
                <select
                  value={icon.platform}
                  onChange={(e) => handleIconUpdate(index, { platform: e.target.value as SocialIcon['platform'] })}
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PLATFORM_OPTIONS.map((key) => (
                    <option key={key} value={key}>
                      {t(`settings.socialIcons.platforms.${key}` as const)}
                    </option>
                  ))}
                </select>

                {/* Delete button */}
                <button
                  onClick={() => handleRemoveIcon(index)}
                  className="p-2 bg-red-600 hover:bg-red-700 rounded text-white transition-colors"
                  title={t('settings.common.remove')}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <input
                type="text"
                value={icon.url}
                onChange={(e) => handleIconUpdate(index, { url: e.target.value })}
                placeholder={t('settings.socialIcons.urlPlaceholder')}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {icon.platform === 'custom' && (
                <input
                  type="text"
                  value={icon.iconUrl || ''}
                  onChange={(e) => handleIconUpdate(index, { iconUrl: e.target.value })}
                  placeholder={t('settings.socialIcons.iconUrlPlaceholder')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.socialIcons.iconSize')}</label>
        <input
          type="range"
          min="24"
          max="64"
          value={component.settings.iconSize}
          onChange={(e) => handleUpdate({ iconSize: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.iconSize}px</div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.socialIcons.iconColor')}</label>
        <ColorPicker
          color={component.settings.iconColor}
          onChange={(color) => handleUpdate({ iconColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.socialIcons.spacing')}</label>
        <input
          type="range"
          min="8"
          max="40"
          value={component.settings.spacing}
          onChange={(e) => handleUpdate({ spacing: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.spacing}px</div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.alignment')}</label>
        <div className="flex gap-2">
          <button
            onClick={() => handleUpdate({ alignment: 'left' })}
            className={`flex-1 p-2 rounded ${component.settings.alignment === 'left' ? 'bg-blue-600 text-white' : 'bg-gray-800'}`}
            title={t('settings.common.alignLeft')}
          >
            <AlignLeft size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'center' })}
            className={`flex-1 p-2 rounded ${component.settings.alignment === 'center' ? 'bg-blue-600 text-white' : 'bg-gray-800'}`}
            title={t('settings.common.alignCenter')}
          >
            <AlignCenter size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'right' })}
            className={`flex-1 p-2 rounded ${component.settings.alignment === 'right' ? 'bg-blue-600 text-white' : 'bg-gray-800'}`}
            title={t('settings.common.alignRight')}
          >
            <AlignRight size={18} className="mx-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="0"
          max="60"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.padding}px</div>
      </div>
    </div>
  );
};
